import { memo } from 'react';

import { Stack, Tooltip, Typography } from '@mui/material';
import { Check, Clock, X } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { AssignmentStatusChipFragment } from '@graphql/__generated__/graphql';
import {
  chipTypographyStyles,
  createChipStyle,
  useChipStatusColors,
} from '@modules/_shared/components/feed-v2/components/statusChipStyles';
import { tooltipProps } from '@modules/_shared/components/feed-v2/constants/styles';
import {
  formatDueDate,
  formatFullDate,
} from '@modules/_shared/components/feed-v2/utils/formatters';
import { useLocale } from '@shared/hooks/locale';

export const ASSIGNMENT_STATUS_CHIP_FRAGMENT = gql(/* GraphQL */ `
  fragment AssignmentStatusChip on activities {
    id
    done
    due_at
    success
    consolidated_done_at
  }
`);

type Props = {
  activity: AssignmentStatusChipFragment;
};

export const AssignmentStatusChip = memo(({ activity }: Props) => {
  const { t, locale } = useLocale();
  const statusColorsMap = useChipStatusColors();

  if (activity.done && activity.success) {
    // Accepted
    const color = statusColorsMap.success;
    return (
      <Tooltip
        title={
          activity.consolidated_done_at
            ? formatFullDate(locale, activity.consolidated_done_at)
            : t('Accepted')
        }
        {...tooltipProps}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={[
            {
              alignItems: 'center',
            },
            createChipStyle('success'),
          ]}
        >
          <Check size={12} color={color} />
          <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
            {t('Accepted')}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  if (activity.done && !activity.success) {
    // Rejected
    const color = statusColorsMap.error;
    return (
      <Tooltip
        title={
          activity.consolidated_done_at
            ? formatFullDate(locale, activity.consolidated_done_at)
            : t('Rejected')
        }
        {...tooltipProps}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={[
            {
              alignItems: 'center',
            },
            createChipStyle('error'),
          ]}
        >
          <X size={12} color={color} />
          <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
            {t('Rejected')}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  if (!activity.done && activity.due_at) {
    // Pending with due date
    const dueResult = formatDueDate(locale, activity.due_at, t);
    const color = dueResult.overdue
      ? statusColorsMap.error
      : statusColorsMap.pending;
    return (
      <Tooltip
        title={formatFullDate(locale, activity.due_at)}
        {...tooltipProps}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={[
            {
              alignItems: 'center',
            },
            createChipStyle(dueResult.overdue ? 'error' : 'pending'),
          ]}
        >
          <Clock size={12} color={color} />
          <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
            {dueResult.text}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  if (!activity.done) {
    // Pending without due date
    const color = statusColorsMap.pending;
    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={[
          {
            alignItems: 'center',
          },
          createChipStyle('pending'),
        ]}
      >
        <Clock size={12} color={color} />
        <Typography variant="caption" sx={{ color, ...chipTypographyStyles }}>
          {t('Pending')}
        </Typography>
      </Stack>
    );
  }

  return null;
});
